import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPinnedBadges, evaluateBadges, getBadgeDetail, claimBadgeNft, pinBadge } from "../../api/user";
import { toast } from "react-toastify";

const TIER_RING = {
  1: "ring-2 ring-yellow-500",
  2: "ring-2 ring-teal-600",
  3: "ring-2 ring-slate-400",
};

/*
 * Dashboard preview of a user's 3 pinned (or top prestige) badges.
 * Tapping a badge opens the badge detail card inline.
 */
const DashboardBadgePreview = () => {
  const [badges, setBadges] = useState([]);
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        try {
          await evaluateBadges();
        } catch (e) {
          /* non-fatal */
        }
        const data = await getPinnedBadges();
        if (mounted) setBadges(Array.isArray(data) ? data.slice(0, 3) : []);
      } catch (e) {
        if (mounted) setBadges([]);
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const openDetail = async (badge) => {
    setSelected(badge);
    try {
      setDetailLoading(true);
      const full = await getBadgeDetail(badge.uuid);
      if (full) setSelected(full);
    } catch (e) {
      /* keep summary */
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePin = async (badge) => {
    try {
      const next = !badge.is_pinned;
      const res = await pinBadge(badge.uuid, next);
      if (res?.success === false) { toast.error(res.message); return; }
      toast.success(res?.message || "Updated");
      if (!next) {
        // Unpinned — remove from preview list and close detail card
        setBadges((prev) => prev.filter((b) => b.uuid !== badge.uuid));
        setSelected(null);
      } else {
        // Pinned — re-fetch so the list stays accurate (max 3)
        try {
          const data = await getPinnedBadges();
          setBadges(Array.isArray(data) ? data.slice(0, 3) : []);
        } catch (e) { /* keep current list */ }
        setSelected((s) => s && s.uuid === badge.uuid ? { ...s, is_pinned: next } : s);
      }
    } catch (e) {
      toast.error("Could not update pin");
    }
  };

  const handleClaimNft = async (badge) => {
    try {
      const res = await claimBadgeNft(badge.uuid);
      toast.info(res?.message || "NFT minting is coming soon.");
    } catch (e) {
      toast.info("NFT minting is coming soon.");
    }
  };

  if (!ready) return null;

  const slots = [0, 1, 2].map(i => badges[i] ?? null);

  return (
    <>
      <div className="px-2 mx-auto mainCard">
        <div className="bg-white rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[#0E5B5B] mr-1">Badges</span>
            <div className="flex -space-x-2">
              {slots.map((b, i) =>
                b ? (
                  <button
                    key={b.uuid}
                    type="button"
                    onClick={() => openDetail(b)}
                    className="focus:outline-none"
                    title={b.name}
                  >
                    <img
                      src={b.image}
                      alt={b.name}
                      className={`h-10 w-10 rounded-full object-cover bg-white ring-2 ring-white border border-gray-100 ${TIER_RING[b.tier] || ""}`}
                      onError={(e) => { e.target.style.visibility = "hidden"; }}
                    />
                  </button>
                ) : (
                  <div
                    key={`empty-${i}`}
                    className="h-10 w-10 rounded-full bg-gray-100 border border-dashed border-gray-300 ring-2 ring-white"
                  />
                )
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="text-xs font-semibold text-[#0E5B5B]"
          >
            View all
          </button>
        </div>
      </div>

      {/* Badge detail card — opens on badge tap */}
      {selected && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white w-full max-w-[420px] rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#0E5B5B]">
                {selected.tier_label || "Achievement"}
              </span>
              <button onClick={() => setSelected(null)} className="text-2xl leading-none text-[#707070]">&times;</button>
            </div>
            <div className="flex flex-col items-center">
              <img
                src={selected.image}
                alt={selected.name}
                className={`h-28 w-28 rounded-full object-cover bg-white ${TIER_RING[selected.tier] || ""}`}
              />
              <h2 className="text-lg font-bold mt-3 text-center">{selected.name}</h2>
              {selected.tier_label && (
                <span className="mt-1 px-3 py-0.5 rounded-full text-xs font-semibold bg-[#0E5B5B] text-white">
                  {selected.tier_label}
                </span>
              )}
              {selected.description && (
                <p className="text-sm text-[#707070] text-center mt-1">{selected.description}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-center">
              <div className="bg-[#f5f7f7] rounded-lg py-2">
                <p className="text-[11px] text-[#707070]">Your number</p>
                <p className="text-base font-bold text-[#0E5B5B]">
                  {selected.scarcity_number ? `#${selected.scarcity_number}` : "—"}
                </p>
              </div>
              <div className="bg-[#f5f7f7] rounded-lg py-2">
                <p className="text-[11px] text-[#707070]">Total issued</p>
                <p className="text-base font-bold text-[#0E5B5B]">
                  {selected.total_issued ?? "—"}{selected.max_supply ? ` / ${selected.max_supply}` : ""}
                </p>
              </div>
            </div>
            <div className="mt-3 text-[12px] text-[#555] space-y-1">
              {selected.earned_at && (
                <div className="flex justify-between">
                  <span className="text-[#9a9a9a]">Earned</span>
                  <span>{new Date(selected.earned_at).toLocaleString()}</span>
                </div>
              )}
              {selected.competition_type ? (
                <div className="flex justify-between">
                  <span className="text-[#9a9a9a]">Competition</span>
                  <span className="capitalize">{String(selected.competition_type).replace(/_/g, ' ')}</span>
                </div>
              ) : null}
              {selected.league_name ? (
                <div className="flex justify-between">
                  <span className="text-[#9a9a9a]">Source</span>
                  <span>{selected.league_name}</span>
                </div>
              ) : null}
              {selected.finish_position ? (
                <div className="flex justify-between">
                  <span className="text-[#9a9a9a]">Position</span>
                  <span>{selected.finish_position}{selected.total_participants ? ` of ${selected.total_participants}` : ""}</span>
                </div>
              ) : null}
              {selected.scarcity_number ? (
                <div className="flex justify-between">
                  <span className="text-[#9a9a9a]">Issued</span>
                  <span>{selected.scarcity_number}{selected.total_issued_at_earn ? ` of ${selected.total_issued_at_earn}` : ""} ever</span>
                </div>
              ) : null}
            </div>
            {selected.metadata_hash && (
              <p className="text-[10px] text-[#bdbdbd] text-center mt-1 break-all">
                Proof: {String(selected.metadata_hash).slice(0, 24)}…
              </p>
            )}
            <button
              type="button"
              disabled={!selected.nft_claimable}
              onClick={() => selected.nft_claimable && handleClaimNft(selected)}
              className={`w-full mt-4 py-2 rounded-lg text-sm font-semibold ${selected.nft_claimable ? "bg-[#0E5B5B] text-white" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
            >
              {selected.nft_claimable ? "Claim NFT" : "Claim NFT — Coming Soon"}
            </button>
            {!selected.nft_claimable && (
              <p className="text-[11px] text-[#9a9a9a] text-center mt-2 leading-snug">
                QUSHi locks your badge on the blockchain permanently. QUSHi covers gas. One tap.
              </p>
            )}
            <button
              type="button"
              onClick={() => handlePin(selected)}
              className="w-full mt-2 py-2 rounded-lg text-sm font-semibold border border-[#0E5B5B] text-[#0E5B5B]"
            >
              {selected.is_pinned ? "Unpin from dashboard" : "Pin to dashboard"}
            </button>
            {detailLoading && (
              <p className="text-[11px] text-center text-[#9a9a9a] mt-2">Loading details…</p>
            )}
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="w-full mt-3 text-sm text-[#9a9a9a] text-center"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardBadgePreview;
